import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  LuCheck,
  LuExternalLink,
  LuIndianRupee,
  LuPackageSearch,
  LuSearch,
} from "react-icons/lu";

import MaterialPhoto from "./MaterialPhoto";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import { EmptyState, LoadingState } from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldIconClass,
  fieldInputClass,
} from "../ui/styles";
import { useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { closeAfterQty } from "../../static/cuttingLine";
import { compareCodes } from "../../static/itemCode";
import { sanitizeRate } from "../../static/processRate";
import {
  filledLines,
  formatAmount,
  formatGujaratiDateTime,
  formatQty,
  lineKey,
  orderLineKey,
  priceText,
  purchaseLineKey,
  sanitizeQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import {
  IMaterialLineInput,
  LineHintProps,
  MaterialOptionProps,
  MaterialOptionSizeProps,
} from "../../types/materialLine";
import { OrderLineProps } from "../../types/materialOrder";

type Hints = Record<string, LineHintProps>;

type LinePatch = Partial<
  Pick<IMaterialLineInput, "qty" | "price" | "close_order">
>;

// One card per material, for its first size: the materials module doesn't show sizes (a new material has the one 3660
// size), and a material made before that with more sizes also gets a card for any other size a line on the form
// already has. A material without a size still gets a card, which can't be picked. On a purchase, an open order's line
// gets a card of its own (orderLine), and its material size gets no plain card.
type CardOption = {
  key: string;
  material: MaterialOptionProps;
  size: MaterialOptionSizeProps | null;
  orderLine?: OrderLineProps;
};

// One empty list for every material no item is made from, so its card's props stay the same between renders.
const noCodes: string[] = [];

// A label with its box on the right; the box gives way on a narrow (phone) card.
const rowClass = "flex items-center justify-between gap-4";

const rowLabelClass = "shrink-0 text-xs font-medium text-gray-600";

const rowBoxClass = "relative w-28 min-w-0";

const cardInputClass =
  "h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-right text-sm font-semibold placeholder:text-muted-foreground focus:border-[#113F67] focus:outline-none focus:ring-1 focus:ring-[#113F67]";

// The reference card's checkbox: navy border, navy with a white tick when ticked.
const checkboxClass =
  "peer h-4 w-4 cursor-pointer appearance-none rounded border border-[#113F67] bg-transparent checked:bg-[#113F67] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-1";

const materialPath = (material: MaterialOptionProps) =>
  `${route.materials}?search=${encodeURIComponent(material.name)}`;

const orderPath = (orderId: number) =>
  route.materialOrderDetail.replace(":id", orderId as unknown as string);

// "પહેલાનો રેટ" of a purchase card: the party's last purchase price of this material.
const LastRateRow = ({ hint }: { hint: LineHintProps | undefined }) => (
  <div className="flex items-center justify-between gap-2 text-xs">
    <span className={rowLabelClass}>પહેલાનો રેટ:</span>
    <span className="text-sm leading-4 text-gray-400">
      {(hint?.lastPrice ?? null) !== null
        ? `₹${formatAmount(hint?.lastPrice)}`
        : "-"}
    </span>
  </div>
);

// How much of an order's lines has come: a bar and "R/Q મળેલ", " • બંધ" once a purchase closed the line. Its spacing
// (mt-2, leading-3) was matched pixel for pixel to the user's reference card.
const ReceivedBar = ({
  received,
  qty,
  closed,
}: {
  received: number;
  qty: number;
  closed?: boolean;
}) => {
  const progress = qty > 0 ? Math.min(100, (received / qty) * 100) : 0;

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-green-600"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="shrink-0 text-xs leading-3 text-gray-500">
        {formatQty(received)}/{formatQty(qty)} મળેલ
        {closed && " • બંધ"}
      </span>
    </div>
  );
};

// An order card's order, with a link to it, and how much of its line has come so far. Its spacing (the 3px too) was
// matched pixel for pixel to the user's reference card.
const OrderLineInfo = ({
  orderLine,
  canOpenOrder,
}: {
  orderLine: OrderLineProps;
  canOpenOrder: boolean;
}) => (
  <>
    <div className="mt-[3px] flex items-center gap-1 leading-6 text-[#113F67]">
      <span className="truncate">
        <span className="text-sm font-semibold">
          ઓર્ડર #{orderLine.order_id}
        </span>{" "}
        <span className="text-xs text-gray-500">
          ({formatGujaratiDateTime(orderLine.order_date)})
        </span>
      </span>
      {canOpenOrder && (
        <a
          href={orderPath(orderLine.order_id)}
          target="_blank"
          rel="noopener noreferrer"
          title="ઓર્ડર જુઓ"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="shrink-0 hover:opacity-70"
        >
          <LuExternalLink className="h-[15px] w-[15px]" />
        </a>
      )}
    </div>

    <ReceivedBar
      received={orderLine.received_qty}
      qty={orderLine.qty}
      closed={orderLine.closed}
    />
  </>
);

// An order-form card's material still awaited on the party's other open orders: the qty, and the bar of how much of
// those lines has come, as on the purchase picker's order cards.
const PendingOrderInfo = ({ hint }: { hint: LineHintProps }) => (
  <div className="mt-3">
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className={rowLabelClass}>બાકી ઓર્ડર જથ્થો:</span>
      <span className="text-sm font-semibold leading-4 text-[#113F67]">
        {formatQty(hint.pending)}
      </span>
    </div>

    <ReceivedBar received={hint.openReceived} qty={hint.openOrdered} />
  </div>
);

type CardProps = {
  option: CardOption;
  // The codes of the main items made from the material, in the item list's order.
  itemCodes: string[];
  line: IMaterialLineInput | undefined;
  hint: LineHintProps | undefined;
  isPurchase: boolean;
  withPrice: boolean;
  canOpenMaterial: boolean;
  canOpenOrder: boolean;
  onToggle: (option: CardOption) => void;
  onChangeLine: (key: string, patch: LinePatch) => void;
};

// Gets only its own line, so typing in one card doesn't redraw the others. Its sizes and spacing (the odd px values
// too) were matched pixel for pixel to the user's reference cards, so keep them when restyling.
const MaterialCard = React.memo(
  ({
    option,
    itemCodes,
    line,
    hint,
    isPurchase,
    withPrice,
    canOpenMaterial,
    canOpenOrder,
    onToggle,
    onChangeLine,
  }: CardProps) => {
    const { material, size, orderLine } = option;
    const isSelected = !!line;
    // A material received against the order being edited stays on it.
    const isLocked = isSelected && (hint?.received || 0) > 0;
    const canToggle = !!size && !isLocked;
    // A plain purchase card says "ઓર્ડર નથી" (receiving it reduces no order), with room above its rate row, like the
    // reference. An order-form card shows what the party's other open orders still await of it, and nothing when none
    // do.
    const showNoOrder = !orderLine && isPurchase;
    const pendingHint =
      !orderLine && !isPurchase && hint && (hint.pending || 0) > 0
        ? hint
        : null;
    const name = material.name;

    const qtyRef = useRef<HTMLInputElement>(null);
    // Set when the user ticks this card, so its qty box takes the focus once it is there. Cards already ticked when
    // the picker opens leave the focus alone.
    const focusQty = useRef(false);

    useEffect(() => {
      if (line && focusQty.current) {
        focusQty.current = false;
        qtyRef.current?.focus();
      }
    }, [line]);

    const handleToggle = () => {
      if (!canToggle) return;

      focusQty.current = !isSelected;
      onToggle(option);
    };

    return (
      <div
        className={cx(
          "overflow-hidden rounded-lg border bg-white",
          isSelected
            ? "border-green-600 ring-1 ring-green-600"
            : "border-gray-200"
        )}
      >
        <div
          role="button"
          tabIndex={0}
          aria-pressed={isSelected}
          title={isLocked ? "મળી ગયેલ મટીરીયલ કાઢી શકાશે નહીં" : undefined}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleToggle();
            }
          }}
          className={cx(
            "block p-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#113F67]",
            canToggle ? "cursor-pointer" : "cursor-default"
          )}
        >
          <div className="relative">
            <MaterialPhoto
              name={material.name}
              imageUrl={material.image_url}
              className="aspect-[4/3] w-full rounded-md"
            />
            <span
              aria-hidden="true"
              className={cx(
                "absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full",
                isSelected ? "bg-green-600 text-white" : "bg-white shadow-sm"
              )}
            >
              {isSelected && (
                <LuCheck className="h-3.5 w-3.5" strokeWidth={3} />
              )}
            </span>
          </div>

          {/* The name, with the ↗ in the middle of its line. */}
          <div className="mt-2 flex items-start gap-1 text-sm font-semibold leading-6 text-[#113F67]">
            <span className="flex min-w-0 flex-wrap items-baseline gap-x-1">
              <span className="truncate" title={material.name}>
                {material.name}
              </span>
            </span>
            {canOpenMaterial && (
              <a
                href={materialPath(material)}
                target="_blank"
                rel="noopener noreferrer"
                title="મટીરીયલ જુઓ"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                className="mt-[4.5px] shrink-0 hover:opacity-70"
              >
                <LuExternalLink className="h-[15px] w-[15px]" />
              </a>
            )}
          </div>

          {/* The codes of the main items made from the material, all of them, comma-separated and wrapping as they
              need; a code never breaks at its hyphen. Without any the line stays, empty, at its one-line height. */}
          <div
            className={cx(
              "min-h-4 break-words text-xs leading-4",
              size ? "text-gray-500" : "text-red-600"
            )}
          >
            {size
              ? itemCodes.map((code, index) => (
                  <React.Fragment key={code}>
                    {index > 0 && ", "}
                    <span className="whitespace-nowrap">{code}</span>
                  </React.Fragment>
                ))
              : "પસંદ કરી શકાશે નહીં"}
          </div>

          {orderLine && (
            <OrderLineInfo orderLine={orderLine} canOpenOrder={canOpenOrder} />
          )}

          {pendingHint && <PendingOrderInfo hint={pendingHint} />}

          {showNoOrder && (
            <div
              className={cx(
                "mt-px text-xs text-gray-400",
                withPrice && "pb-[13px]"
              )}
            >
              ઓર્ડર નથી
            </div>
          )}
        </div>

        {line ? (
          <div
            className={cx(
              "space-y-2 border-t border-gray-200 bg-gray-50 px-2.5 pt-[13px]",
              orderLine ? "pb-2.5" : "pb-[11px]"
            )}
          >
            {withPrice && <LastRateRow hint={hint} />}

            {isLocked && (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className={rowLabelClass}>મળેલ:</span>
                <span className="text-sm leading-4 text-gray-600">
                  {formatQty(hint?.received)}
                  {hint?.closed && " • બંધ"}
                </span>
              </div>
            )}

            <label className={rowClass}>
              <span className={rowLabelClass}>જથ્થો:</span>
              <span className={rowBoxClass}>
                <input
                  ref={qtyRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label={`${name} જથ્થો`}
                  value={line.qty}
                  onChange={(event) => {
                    const qty = sanitizeQty(event.target.value);

                    // An order card's qty that comes to make up its order line (with what other purchases received)
                    // ticks "ઓર્ડર બંધ", and one that no longer does unticks it; a tick set by hand otherwise stays.
                    onChangeLine(
                      option.key,
                      orderLine
                        ? {
                            qty,
                            close_order: closeAfterQty(
                              orderLine.qty,
                              orderLine.received_qty,
                              line.qty,
                              qty,
                              line.close_order
                            ),
                          }
                        : { qty }
                    );
                  }}
                  className={cardInputClass}
                />
              </span>
            </label>

            {withPrice && (
              <label className={rowClass}>
                <span className={rowLabelClass}>રેટ:</span>
                <span className={rowBoxClass}>
                  <LuIndianRupee className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    aria-label={`${name} રેટ`}
                    placeholder="0.00"
                    value={line.price}
                    onChange={(event) =>
                      onChangeLine(option.key, {
                        price: sanitizeRate(event.target.value),
                      })
                    }
                    className={cx(cardInputClass, "pl-8")}
                  />
                </span>
              </label>
            )}

            {orderLine && (
              <label className={cx(rowClass, "h-6 cursor-pointer")}>
                <span className={rowLabelClass}>ઓર્ડર બંધ:</span>
                <span className="relative flex h-4 w-4 shrink-0">
                  <input
                    type="checkbox"
                    aria-label={`${name} ઓર્ડર બંધ`}
                    checked={line.close_order}
                    onChange={(event) =>
                      onChangeLine(option.key, {
                        close_order: event.target.checked,
                      })
                    }
                    className={checkboxClass}
                  />
                  <LuCheck
                    className="pointer-events-none absolute inset-0 m-auto hidden h-3 w-3 text-white peer-checked:block"
                    strokeWidth={3}
                  />
                </span>
              </label>
            )}
          </div>
        ) : (
          withPrice && (
            <div className="px-2.5 pb-3">
              <LastRateRow hint={hint} />
            </div>
          )
        )}
      </div>
    );
  }
);

MaterialCard.displayName = "MaterialCard";

type Props = {
  materials: MaterialOptionProps[];
  materialsLoading: boolean;
  // The form's lines when the picker opens; the picker hands its lines back when it closes.
  initialLines: IMaterialLineInput[];
  hints: Hints;
  // A purchase picks the party's open order lines (oldest order first) before the other materials.
  isPurchase: boolean;
  orderLines?: OrderLineProps[];
  // Only a purchase line has a price, and only for a user who may see rates.
  withPrice: boolean;
  onClose: (lines: IMaterialLineInput[]) => void;
};

// Picks materials as cards, like a catalogue: a picked card is one line, with its qty (and on a purchase its
// price). There is no separate save; closing the picker in any way hands its lines to the form. Mount it only while
// it is open.
const MaterialPickerModal = ({
  materials,
  materialsLoading,
  initialLines,
  hints,
  isPurchase,
  orderLines,
  withPrice,
  onClose,
}: Props) => {
  const loggingUser = useAppSelector(getLoggingUser);
  const canOpenMaterial = hasPermission(
    loggingUser?.permissions,
    permission.materialsRead
  );
  const canOpenOrder = hasPermission(
    loggingUser?.permissions,
    permission.materialOrdersRead
  );

  const [lines, setLines] = useState<IMaterialLineInput[]>(() =>
    filledLines(initialLines)
  );
  const [search, setSearch] = useState("");

  // The lines already on the form come first, and an order line already on it keeps its card even when nothing is
  // pending on it any more. They keep their place while picking, so no card moves under the pointer.
  const [openedWith] = useState(
    () => new Set(filledLines(initialLines).map(purchaseLineKey))
  );

  // The open order lines still awaited, and those already on the form.
  const orderOptions = useMemo(() => {
    const materialById = new Map(
      materials.map((material) => [material.id, material])
    );

    return (orderLines || []).flatMap((orderLine): CardOption[] => {
      const key = orderLineKey(orderLine.id);
      const material = materialById.get(orderLine.material_id);
      const size = material?.sizes.find(
        (item) => item.id === orderLine.material_size_id
      );
      const isOpen = orderLine.pending_qty > 0 && !orderLine.closed;

      return material && size && (isOpen || openedWith.has(key))
        ? [{ key, material, size, orderLine }]
        : [];
    });
  }, [materials, orderLines, openedWith]);

  // A material's first size (sizes come smallest first), and any other size a line on the form already has. A size with
  // an order card gets no plain card, unless the form has it without an order line.
  const plainOptions = useMemo(() => {
    const orderSizeKeys = new Set(
      orderOptions.map((option) => lineKey(option.material.id, option.size!.id))
    );

    return materials
      .flatMap((material): CardOption[] =>
        material.sizes.length > 0
          ? material.sizes
              .filter(
                (size, index) =>
                  index === 0 || openedWith.has(lineKey(material.id, size.id))
              )
              .map((size) => ({
                key: lineKey(material.id, size.id),
                material,
                size,
              }))
          : [{ key: lineKey(material.id, 0), material, size: null }]
      )
      .filter(
        (option) => !orderSizeKeys.has(option.key) || openedWith.has(option.key)
      );
  }, [materials, orderOptions, openedWith]);

  const lineByKey = useMemo(
    () => new Map(lines.map((line) => [purchaseLineKey(line), line])),
    [lines]
  );

  // Each material's main item codes, in the item list's order.
  const codesByMaterial = useMemo(
    () =>
      new Map(
        materials.map((material) => [
          material.id,
          [...(material.item_codes || [])].sort(compareCodes),
        ])
      ),
    [materials]
  );

  // A picked card comes right after the material's other lines, so they stay together on the form; on a purchase it
  // starts with the party's last purchase price, for a user who may see rates.
  const handleToggle = useCallback(
    (option: CardOption) => {
      if (!option.size) return;

      const { material, size, orderLine } = option;

      setLines((current) => {
        if (current.some((line) => purchaseLineKey(line) === option.key)) {
          return current.filter((line) => purchaseLineKey(line) !== option.key);
        }

        const line: IMaterialLineInput = {
          material_id: material.id,
          material_size_id: size.id,
          order_item_id: orderLine?.id || 0,
          order_id: orderLine?.order_id || 0,
          close_order: false,
          qty: "",
          price: withPrice
            ? priceText(hints[lineKey(material.id, size.id)]?.lastPrice)
            : "",
        };
        const last = current
          .map((item) => item.material_id)
          .lastIndexOf(material.id);

        return last === -1
          ? [...current, line]
          : [...current.slice(0, last + 1), line, ...current.slice(last + 1)];
      });
    },
    [hints, withPrice]
  );

  const handleChangeLine = useCallback((key: string, patch: LinePatch) => {
    setLines((current) =>
      current.map((line) =>
        purchaseLineKey(line) === key ? { ...line, ...patch } : line
      )
    );
  }, []);

  const query = search.trim().toLowerCase();

  // Order cards first, then the plain cards already on the form, then the rest. A search finds a card by what it shows:
  // the material's name, its item codes, and on an order card its order number ("#12").
  const visibleOptions = useMemo(() => {
    const matches = (option: CardOption) =>
      !query ||
      [
        option.material.name,
        (codesByMaterial.get(option.material.id) || noCodes).join(", "),
        option.orderLine ? `#${option.orderLine.order_id}` : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);

    const plain = plainOptions.filter(matches);

    return [
      ...orderOptions.filter(matches),
      ...plain.filter((option) => openedWith.has(option.key)),
      ...plain.filter((option) => !openedWith.has(option.key)),
    ];
  }, [orderOptions, plainOptions, openedWith, query, codesByMaterial]);

  const handleClose = () => onClose(lines);

  return (
    <Dialog open onClose={handleClose}>
      <DialogContent
        size="wide"
        className={cx(
          "font-inter w-full grid-rows-[auto_auto_minmax(0,1fr)_auto] sm:h-[90vh] sm:max-w-[calc(100vw-4rem)]",
          dialogMobileContentClass
        )}
      >
        <DialogHeader
          className={cx(
            "border-b border-[#113F67] pb-4",
            dialogMobileHeaderClass
          )}
        >
          <div className="flex items-center gap-4 pr-6">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#113F67]/10">
              <LuPackageSearch className="h-6 w-6 text-[#113F67]" />
            </span>
            <h2 className={cx(dialogTitleClass, "mb-2")}>મટીરીયલ પસંદ કરો</h2>
          </div>
        </DialogHeader>

        <div className="relative max-sm:mx-6 max-sm:mt-4">
          <LuSearch className={fieldIconClass} />
          <Input
            type="text"
            value={search}
            placeholder="મટીરીયલ નું નામ અથવા આઈટમ કોડ શોધો..."
            aria-label="મટીરીયલ શોધો"
            className={fieldInputClass()}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className={cx("min-h-0 overflow-y-auto", dialogMobileBodyClass)}>
          {materialsLoading && materials.length === 0 ? (
            <LoadingState />
          ) : visibleOptions.length === 0 ? (
            <EmptyState
              label={
                materials.length === 0
                  ? "મટીરીયલ મળ્યા નથી, પહેલા મટીરીયલ માસ્ટર માં મટીરીયલ ઉમેરો"
                  : "શોધ મુજબ કોઈ મટીરીયલ મળ્યું નથી"
              }
            />
          ) : (
            <div className="grid grid-cols-2 items-start gap-4 p-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {visibleOptions.map((option) => (
                <MaterialCard
                  key={option.key}
                  option={option}
                  itemCodes={codesByMaterial.get(option.material.id) || noCodes}
                  line={lineByKey.get(option.key)}
                  hint={
                    hints[
                      lineKey(option.material.id, option.size?.id || 0)
                    ]
                  }
                  isPurchase={isPurchase}
                  withPrice={withPrice}
                  canOpenMaterial={canOpenMaterial}
                  canOpenOrder={canOpenOrder}
                  onToggle={handleToggle}
                  onChangeLine={handleChangeLine}
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter
          className={cx("items-center justify-between", dialogMobileFooterClass)}
        >
          <span className="text-sm font-semibold text-[#113F67]">
            પસંદ કરેલ મટીરીયલ: {lines.length}
          </span>

          <Button variant="outline" className="min-w-40" onClick={handleClose}>
            બંધ કરો
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialPickerModal;
